import { hasAcceptedKombiTerms, toKombiSession } from './session';
import { kombiStateFromHttp } from './states';
import type { KombiDataState, KombiSessionView } from '../viewModels';

export type KombiFetch = (input: string, init?: RequestInit) => Promise<Response>;

/**
 * The routes allowed to host the live Kombi surface, each with its own safe return path. The union is
 * closed on purpose: `returnTo` is handed to the gateway as a redirect target, so a route cannot
 * invent one. `as const satisfies` below is what enforces it — an unlisted path is a type error at
 * the descriptor, not at some later call site.
 */
export interface KombiLiveRoute {
  returnTo:
    | '/'
    | '/admin'
    | '/admin/branding'
    | '/admin/branding/new'
    | `/admin/branding/${string}/edit`
    | '/learning'
    | '/community'
    | '/me'
    | '/dashboard'
    | '/design/kombi-manager-dashboard-live'
    | '/design/kombi-profile-live'
    | `/design/kombi-public-profile-live/${string}`
    | `/users/${string}`
    | '/design/kombi-community-live'
    | `/design/kombi-catalogue-live/${'ai-agents' | 'apps' | 'automations'}`
    | '/share'
    | '/design/kombi-live'
    | `/catalogue/${'ai-agents' | 'apps' | 'automations'}`
    | `/catalogue/automations/${string}/edit`
    | `/design/kombi-automation-detail-live/${string}`
    | `/tools/${string}`;
}

export const KOMBI_LIVE_ROUTES = {
  production: { returnTo: '/' },
  reference: { returnTo: '/design/kombi-live' },
  aiAgents: { returnTo: '/catalogue/ai-agents' },
  apps: { returnTo: '/catalogue/apps' },
  automations: { returnTo: '/catalogue/automations' },
  admin: { returnTo: '/admin' },
  branding: { returnTo: '/admin/branding' },
  brandingNew: { returnTo: '/admin/branding/new' },
  learning: { returnTo: '/learning' },
  community: { returnTo: '/community' },
  // Production destinations for the promoted profile and manager dashboard, exactly like `admin`
  // above: the canonical URL the nav has always pointed at, so nothing needs repointing. The
  // `*Live` descriptors below stay for the private `/design` reference routes.
  managerDashboard: { returnTo: '/dashboard' },
  profile: { returnTo: '/me' },
  managerDashboardLive: { returnTo: '/design/kombi-manager-dashboard-live' },
  profileLive: { returnTo: '/design/kombi-profile-live' },
  communityLive: { returnTo: '/design/kombi-community-live' },
  referenceAiAgents: { returnTo: '/design/kombi-catalogue-live/ai-agents' },
  referenceApps: { returnTo: '/design/kombi-catalogue-live/apps' },
  referenceAutomations: { returnTo: '/design/kombi-catalogue-live/automations' },
  /**
   * ONE descriptor for all four steps of the share journey, whose `returnTo` is the journey's entry
   * rather than the step the person happened to be on. A sign-in that landed straight back on
   * `/share/review` would show the review of a draft the browser may no longer hold; `/share` re-enters
   * at Select, and the sessionStorage draft is still there to resume from.
   */
  share: { returnTo: '/share' },
} as const satisfies Record<string, KombiLiveRoute>;

/** Construct the private detail return target only from the router parameter, never request text. */
export function kombiAutomationDetailLiveRoute(toolId: string): KombiLiveRoute {
  return {
    returnTo: `/design/kombi-automation-detail-live/${encodeURIComponent(toolId)}`,
  };
}

/**
 * The PRODUCTION tool-detail return target, `/tools/{toolId}` — the URL the catalogue, the search
 * results, the profile galleries and the kind-1 publish announcement have always pointed at.
 *
 * Built only from the router parameter and encoded, exactly like the private variant above: `returnTo`
 * is handed to the gateway as a redirect target, so a tool id that arrived in a URL must never be able
 * to widen it.
 */
export function kombiToolDetailRoute(toolId: string): KombiLiveRoute {
  return { returnTo: `/tools/${encodeURIComponent(toolId)}` };
}

/** Safe dynamic return destination for the owner-only automation edit route. */
export function kombiAutomationEditRoute(toolId: string): KombiLiveRoute {
  return { returnTo: `/catalogue/automations/${encodeURIComponent(toolId)}/edit` };
}

/** Safe return destination for a curator editing a server-owned brand preset. */
export function kombiBrandingEditRoute(presetId: string): KombiLiveRoute {
  return { returnTo: `/admin/branding/${encodeURIComponent(presetId)}/edit` };
}

/** Safe return destination for the live public-profile reference route. */
export function kombiPublicProfileLiveRoute(userId: string): KombiLiveRoute {
  return { returnTo: `/design/kombi-public-profile-live/${encodeURIComponent(userId)}` };
}

/** Safe return destination for the canonical public profile. */
export function kombiPublicProfileRoute(userId: string): KombiLiveRoute {
  return { returnTo: `/users/${encodeURIComponent(userId)}` };
}

/** The loop breaker: `?sso=none` marks an arrival that must NOT trigger another silent-SSO attempt. */
const SSO_PARAM = 'sso';
const SSO_NONE = 'none';

/**
 * Where an explicit sign-out lands. It carries the loop breaker, because without it the destination's
 * loader reads the just-cleared session as an ordinary 401 and bounces the browser straight back into a
 * silent-SSO attempt, signing the caller back in against their stated intent.
 */
export function postSignOutDestination(returnTo: string = '/'): string {
  const [path, query = ''] = returnTo.split('?');
  const params = new URLSearchParams(query);
  params.set(SSO_PARAM, SSO_NONE);
  return `${path}?${params.toString()}`;
}

/**
 * How the loader hands the browser to the backend's silent-SSO endpoint. This MUST be a real document
 * navigation: `/auth/login` belongs to the gateway, not to our route table, and react-router's
 * `redirect()` resolves same-origin paths against our own routes — matching nothing and rendering the
 * 404 page instead of ever reaching the server. Every other sign-in affordance in the app is a plain
 * `<a href>` for the same reason. `replace` keeps `/auth/login` out of history, so Back cannot re-enter
 * the attempt. The promise never settles because the document is being torn down: settling it would let
 * the loader go on to paint a signed-out view that flashes before the browser leaves.
 */
export type SilentSsoNavigation = (url: string) => Promise<never>;

const leaveForSilentSso: SilentSsoNavigation = (url) => {
  window.location.replace(url);
  return new Promise<never>(() => {});
};

/**
 * The sole identity probe for every live Kombi route. A 401 preserves the route-specific silent-SSO
 * return destination and the `sso=none` loop breaker; it never claims an absent terms state is valid.
 */
export async function loadKombiSession(
  request: Request,
  fetcher: KombiFetch = fetch,
  route: KombiLiveRoute = KOMBI_LIVE_ROUTES.reference,
  navigate: SilentSsoNavigation = leaveForSilentSso,
): Promise<KombiDataState<KombiSessionView>> {
  let response: Response;
  try {
    response = await fetcher('/api/me', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
  } catch {
    return { kind: 'error', status: 0 };
  }
  if (response.status === 401) {
    if (new URL(request.url).searchParams.get(SSO_PARAM) !== SSO_NONE) {
      return navigate(`/auth/login?prompt=none&returnTo=${encodeURIComponent(route.returnTo)}`);
    }
    return { kind: 'signed-out' };
  }
  if (!response.ok) return kombiStateFromHttp<KombiSessionView>(response, null);
  try {
    return {
      kind: 'ready',
      value: toKombiSession((await response.json()) as Record<string, unknown>),
    };
  } catch {
    return { kind: 'error', status: response.status };
  }
}

/** A resource request is allowed only after the shared session confirms current-terms acceptance. */
export function canLoadKombiProtectedData(session: KombiDataState<KombiSessionView>): boolean {
  return session.kind === 'ready' && hasAcceptedKombiTerms(session.value);
}

/**
 * The reusable loader boundary for every future live Kombi page.
 *
 * A page supplies only its own protected-data read. This function owns the one `/api/me` probe and
 * does not call that page reader until both authentication and terms acceptance are confirmed.
 */
export type KombiProtectedPageData<T> = {
  session: KombiDataState<KombiSessionView>;
  data: T | null;
};

export async function loadKombiProtectedPage<T>(
  request: Request,
  route: KombiLiveRoute,
  loadProtectedData: () => Promise<T>,
  fetcher: KombiFetch = fetch,
  navigate?: SilentSsoNavigation,
): Promise<KombiProtectedPageData<T>> {
  const session = await loadKombiSession(request, fetcher, route, navigate);
  if (!canLoadKombiProtectedData(session)) return { session, data: null };
  return { session, data: await loadProtectedData() };
}
